import { useAnalytics } from "@codat/analytics";
import {
  BinIcon,
  Button,
  ClipboardCopy,
  ClosedEyeIcon,
  EyeIcon,
  InterfaceTooltip,
  Typography,
  useToast,
} from "@codat/orchard-ui";
import React, { FC, useRef } from "react";

import styles from "./ApiKeysTableStyles.module.scss";
import {
  apiKeyToAuthorizationHeader,
  apiKeyToTruncatedAuthorizationHeader,
  getTruncatedString,
  maskedKey,
} from "./helpers";

export interface ApiKeysDataCellProps {
  kind: "auth-header" | "api-key";
  apiKey: string;
  isMasked: boolean;
}

interface DisplayProps {
  displayText: string;
  buttonLabel: string;
  clipboardCopyText: string;
  toastMessage: string;
  eventName: string;
  ariaLabel: string;
}

const getDisplayProps = (props: ApiKeysDataCellProps): DisplayProps => {
  const { kind, isMasked, apiKey } = props;

  if (kind === "auth-header") {
    const displayText = isMasked
      ? "Basic " + maskedKey
      : apiKeyToTruncatedAuthorizationHeader(apiKey);

    return {
      toastMessage: "Authorization header copied to clipboard",
      buttonLabel: "Copy authorization header",
      clipboardCopyText: apiKeyToAuthorizationHeader(apiKey),
      eventName: "Authorization header",
      ariaLabel: "Copy authorization header",
      displayText,
    };
  } else {
    const displayText = isMasked ? maskedKey : getTruncatedString(apiKey);

    return {
      toastMessage: "API key copied to clipboard",
      buttonLabel: "Copy API key",
      clipboardCopyText: apiKey,
      eventName: "API key",
      displayText,
      ariaLabel: "Copy API key",
    };
  }
};

export const ApiKeysDataCell: FC<ApiKeysDataCellProps> = (props) => {
  const { sendEvent } = useAnalytics();
  const { addToast } = useToast();
  const copyRef = useRef<HTMLButtonElement | null>(null);

  const {
    buttonLabel,
    displayText,
    clipboardCopyText,
    toastMessage,
    eventName,
    ariaLabel,
  } = getDisplayProps(props);

  return (
    <InterfaceTooltip placement="top" content={<span>Click to copy</span>}>
      <Button
        noShadow
        className={styles.cellButton}
        data-testid="api-key-cell"
        color="interface"
        label={buttonLabel}
        onClick={() => {
          copyRef.current?.click();
        }}
        tabIndex={-1}
        size="small"
      >
        <Typography
          as="p"
          data-testid="table-cell"
          variant="heading_sm"
          fontVariant="code"
        >
          {displayText}
        </Typography>
        <ClipboardCopy
          ref={copyRef}
          noBackground
          color="interface"
          text={clipboardCopyText}
          label={ariaLabel}
          onClick={(e) => {
            e.stopPropagation();
            addToast({
              variant: "success",
              message: toastMessage,
            });
            sendEvent(`API Keys - ${eventName} - Copied`);
          }}
        />
      </Button>
    </InterfaceTooltip>
  );
};

export interface ApiKeysActionCellProps {
  isMasked: boolean;
  isOnlyKey: boolean;
  index: number;
  onMaskToggle: () => void;
  onDeleteClick: () => void;
}

export const ApiKeysActionCell: FC<ApiKeysActionCellProps> = (props) => {
  const { isMasked, isOnlyKey, index, onMaskToggle, onDeleteClick } = props;

  return (
    <div className={styles.actionCell} data-testid={`cta-${index}`}>
      <Button
        noShadow
        data-testid={`button-delete-${index}`}
        size="small"
        variant="secondary"
        color="negative"
        onClick={onDeleteClick}
        className={styles.deleteButton}
        disabled={isOnlyKey}
      >
        <BinIcon />
      </Button>
      <Button
        noShadow
        size="small"
        variant="secondary"
        color="interface"
        onClick={onMaskToggle}
        data-testid={`show-hide-key-${index}`}
      >
        {isMasked ? <EyeIcon /> : <ClosedEyeIcon />}
      </Button>
    </div>
  );
};
